import { Box, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Visibility } from "@mui/icons-material";
import { useEffect, useMemo } from "react";
import { HookAutoComplete } from "../../../../../../atoms/form-fields/SLFieldAutoComplete";
import { HookRadioButton } from "../../../../../../atoms/form-fields/SLFieldRadioButton";
import { HookCheckBox } from "../../../../../../atoms/form-fields/SLFieldCheckBox";
import BasicAutocomplete from "../../../../../../molecules/autocompletes/basic-autocomplete";
import { group_to_show_cost_price_in_share_profile } from "../../../../../../constants/configConstants";
import { HookSwitch } from "../../../../../../atoms/form-fields/SLFieldSwitch";
import { HookTextField } from "../../../../../../atoms/form-fields/SLFieldTextField";
import { countries } from "../../../../../../utils/countries";
import { price_per_hour_currency_values } from "../../../../../../utils/currencies";
import { useHookFormContext } from "../../../../../../utils/hooks/useHookFormContext";
import { switchStyles } from "../../../../../edit-expert/styles";
import { convertToArchetypeOptions, govt_employee_options, multiplierOptions, sharedAgendaFields } from "../helper";
import { commonInputStyles, inputRow } from "../style";
import CustomCheckbox from "../../../../../../atoms/form-molecules/CustomCheckbox";
import { EXTERNAL_PROFILE_SHARE } from "./shareProfile.constants";

type FormFieldProps = {
  group: string | number | undefined
  ask_for_govt_employee: boolean;
  archetypes?: string[]
  respondedAgendas: any[]
  checkedAgendaIds: any[]
  onClickViewAgendaIcon: (id: number) => void
  handleAgendaCheck: (id: number) => void
  client_id?: number
}

const FormFields = ({ group, ask_for_govt_employee, archetypes, client_id, respondedAgendas, checkedAgendaIds, onClickViewAgendaIcon, handleAgendaCheck }: FormFieldProps) => {
  const { registerState, watch, setValue } = useHookFormContext();

  const [is_govt_employee_value, govt_employee_value, non_us_employee_location_value, is_share_agenda] = watch(["is_govt_employee", "govt_employee", "non_us_employee_location", 'is_share_agenda']);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === "change") {
        if (name === "selling_price") {
          const price =
            value.selling_price.length > 1
              ? Math.round(value.selling_price)
              : value.selling_price;
          setValue("selling_price", price);
        }
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line
  }, [watch]);

  useEffect(() => {
    if (checkedAgendaIds?.length > 0) {
      setValue("is_share_agenda", 'yes');
    } else {
      setValue("is_share_agenda", 'no');
    }
  }, [checkedAgendaIds, setValue]);

  const geographiesList =
    useMemo(() => countries.map((country) => ({ label: country.name, value: country.iso3 })), []);

  return (
    <>
      <Grid item xs={12} sx={inputRow} mt={"1rem"}>
        {group_to_show_cost_price_in_share_profile.includes(Number(group)) && (
          <HookTextField
            {...registerState("cost_price")}
            textFieldProps={{
              ...commonInputStyles,
              label: "Cost Price",
              required: true,
              type: "number",
            }}
          />
        )}
      </Grid>

      <Grid item xs={12} sx={inputRow}>
        <HookTextField
          {...registerState("selling_price")}
          rules={{
            required: { value: true, message: "This field is required" },
          }}
          textFieldProps={{
            ...commonInputStyles,
            label: "Selling Price",
            required: true,
            type: "number",
          }}
        />
      </Grid>

      {/* Currency */}
      <Grid item xs={12} sx={inputRow}>
        <BasicAutocomplete
          label="Currency *"
          registerName="currency"
          options={price_per_hour_currency_values}
          isRequired
        />
      </Grid>

      {/* Multipler */}
      <Grid item xs={12} sx={inputRow}>
        <HookAutoComplete
          {...registerState("multiplier")}
          textFieldProps={{
            label: "Multiplier *",
            size: "small",
          }}
          rules={{
            required: {
              value: true,
              message: "Multiplier is required",
            },
          }}
          autocompleteProps={{
            isOptionEqualToValue: (option, value) =>
              option.value === value.value,
            multiple: false,
            freeSolo: false,
            options: multiplierOptions,
            size: "small",
            style: { backgroundColor: "white" },
          }}
        />
      </Grid>

      {/* External profile share */}
      {client_id && EXTERNAL_PROFILE_SHARE[client_id] && (
        <Grid item xs={12}>
          <HookCheckBox
            {...registerState("do_not_share_outside")}
          label={EXTERNAL_PROFILE_SHARE[client_id]}
          />
        </Grid>
      )}

      {/* archetype */}
      {
        // we only need archetype when client is BridgeTown(id === 238)
        archetypes && archetypes.length > 0 && (
          <Grid item xs={12} sx={inputRow}>
            <HookAutoComplete
              {...registerState("archetypes")}
              textFieldProps={{
                label: "ArcheTypes",
                size: "small",
              }}
              autocompleteProps={{
                isOptionEqualToValue: (option, value) =>
                  option.value === value.value,
                multiple: true,
                freeSolo: false,
                options: convertToArchetypeOptions(archetypes || []),
                renderOption: (props: any, option: any) => {
                  return (
                    <li {...props} key={option.label + Math.random()}>
                      <Tooltip title={option.label}>
                        <span
                          style={{
                            display: "inline-block",
                            maxWidth: "100%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {option.label}
                        </span>
                      </Tooltip>
                    </li>
                  );
                },
                size: "small",
                style: { backgroundColor: "white" },
              }}
            />
          </Grid>
        )
      }

      {ask_for_govt_employee && (
        <>
          {/* Is Govt Employee */}
          <Grid item xs={12} sx={inputRow}>
            <HookSwitch
              {...registerState("is_govt_employee")}
              label="Government Employee:"
              labelProps={{
                sx: switchStyles,
              }}
            />
          </Grid>

          {is_govt_employee_value && (
            <Grid item xs={12} sx={inputRow}>
              <HookRadioButton
                {...registerState("govt_employee")}
                label=""
                radioGroupProps={{
                  sx: {
                    "& .MuiTypography-root": {
                      marginLeft: "-5px !important",
                    },
                  },
                }}
                fields={govt_employee_options}
              />
            </Grid>
          )}

          {/* Country */}

          {govt_employee_value === "Non-US" && (
            <Grid item xs={12} sx={inputRow}>
              <HookAutoComplete
                {...registerState("non_us_employee_location")}
                textFieldProps={{
                  label: "Country",
                  size: "small",
                  required: true,
                }}
                rules={{
                  required: { value: true, message: "This field is required" },
                }}
                autocompleteProps={{
                  isOptionEqualToValue: (option: any, value: any) =>
                    option.value === value.value,
                  size: "small",
                  options: geographiesList,
                  loadingText: "Loading...",
                  style: { backgroundColor: "white" },
                }}
              />
            </Grid>
          )}
        </>
      )}

      {/* Ask if want to share agenda responses as well */}
      {respondedAgendas && respondedAgendas.length > 0 && (
        <Grid item xs={12} sx={inputRow}>
          <HookRadioButton
            {...registerState("is_share_agenda")}
            label="Do you want to share agenda responses with client as well? *"
            rules={{
              required: { value: true, message: "This field is required" },
            }}
            radioGroupProps={{
              style: { display: "flex", gap: "1rem" },
            }}
            fields={sharedAgendaFields}
          />
        </Grid>
      )}
      {/* List of agenda responses */}
      {
        is_share_agenda === 'yes' &&
        respondedAgendas.length > 0 && (
          <Grid item xs={12}>
            <div
              style={{
                marginBottom: 8,
                fontSize: "0.8571428571428571rem",
                color: "rgba(0, 0, 0, 0.6)",
              }}
            >
              Agendas Responded By Expert:
            </div>
            {respondedAgendas.map((agenda: any) => (
              <Box
                key={agenda.id}
                sx={{
                  mb: 1,
                  width: "100%",
                  justifyContent: "space-between",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CustomCheckbox
                  label={
                    <span
                      style={{
                        display: "inline-block",
                        maxWidth: 180,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        verticalAlign: "middle",
                      }}
                      title={agenda.fk_agenda_value.title}
                    >
                      {agenda.fk_agenda_value.title}
                    </span>
                  }
                  checked={checkedAgendaIds?.includes(agenda.id)}
                  onChange={() => handleAgendaCheck(agenda.id)}
                />
                <Visibility
                  sx={{ cursor: "pointer" }}
                  onClick={() => onClickViewAgendaIcon(agenda.id)}
                />
              </Box>
            ))}
          </Grid>
        )}

    </>
  );
};

export default FormFields;